import React from 'react';
import { View, StyleSheet, TouchableOpacity, useWindowDimensions } from 'react-native';
import Text from '../../components/text';
import Feather from '@expo/vector-icons/Feather';
import { COLORS, BORDER_RADIUS, SHADOWS } from '../../constants/theme';

const CompactAccountSection = ({
  title,
  subtitle,
  account,
  onEdit,
  onDelete,
  onLink,
}) => {
  const isLinked = !!account?.branch_id;
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  let defaultSub = 'Tenancy Deposit Scheme Custodial';
  if (title?.includes('SDS')) {
    defaultSub = 'Safe Deposits Scotland Integration Channel';
  } else if (title?.includes('Northern Ireland')) {
    defaultSub = 'Northern Ireland Tenancy Deposit Scheme';
  }

  return (
    <View style={styles.cardContainer}>
      {/* Header Row */}
      <View style={[styles.headerRow, isMobile && styles.headerRowMobile]}>
        <View style={styles.titleLeftGroup}>
          <View style={styles.iconCircle}>
            <Feather name="shield" size={16} color="#6C2BD9" />
          </View>
          <View>
            <Text style={styles.cardTitle}>{title}</Text>
            <Text style={styles.cardSubtitle}>{subtitle || defaultSub}</Text>
          </View>
        </View>

        {isLinked ? (
          <View style={styles.connectedBadge}>
            <Text style={styles.connectedBadgeText}>• CONNECTED</Text>
          </View>
        ) : (
          <TouchableOpacity style={styles.linkBtn} onPress={onLink} activeOpacity={0.85}>
            <Text style={styles.linkBtnText}>Link Account</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Connected Credentials Metadata Box */}
      {isLinked && (
        <View style={styles.metaContainer}>
          <View style={[styles.metaRow, isMobile && styles.metaRowMobile]}>
            <View style={styles.metaCol}>
              <Text style={styles.metaLabel}>BRANCH ID</Text>
              <Text style={styles.metaVal}>{account?.branch_id || '-'}</Text>
            </View>

            <View style={styles.metaCol}>
              <Text style={styles.metaLabel}>MEMBER ID</Text>
              <Text style={styles.metaVal}>{account?.member_id || '-'}</Text>
            </View>

            <View style={[styles.metaCol, { flex: 1.5 }]}>
              <Text style={styles.metaLabel}>API KEY</Text>
              <Text style={styles.metaVal} numberOfLines={1}>
                {account?.api_key || 'FSADK-5378L-PWESD-3268J-PLUTY'}
              </Text>
            </View>
          </View>

          {/* Action Buttons Row */}
          <View style={styles.actionBtnRow}>
            <TouchableOpacity style={styles.disconnectBtn} onPress={onDelete} activeOpacity={0.8}>
              <Text style={styles.disconnectBtnText}>Disconnect</Text>
            </TouchableOpacity>

            <TouchableOpacity style={styles.updateBtn} onPress={onEdit} activeOpacity={0.8}>
              <Text style={styles.updateBtnText}>Update Credentials</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );
};

export default CompactAccountSection;

const styles = StyleSheet.create({
  cardContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: COLORS.border,
    padding: 18,
    marginBottom: 16,
    ...SHADOWS.sm,
  },

  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerRowMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 12,
  },

  titleLeftGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },

  iconCircle: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: '#F5F3FF',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },

  cardTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#111827',
  },
  cardSubtitle: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },

  connectedBadge: {
    backgroundColor: '#D1FAE5',
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  connectedBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#059669',
  },

  linkBtn: {
    backgroundColor: '#6C2BD9',
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 16,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  linkBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#FFFFFF',
  },

  /* METADATA BOX */
  metaContainer: {
    marginTop: 14,
    paddingTop: 14,
    borderTopWidth: 1,
    borderColor: '#F3F4F6',
  },

  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FAFAFA',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: BORDER_RADIUS.md,
    padding: 12,
    gap: 16,
    marginBottom: 12,
  },
  metaRowMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 10,
  },

  metaCol: {
    flex: 1,
  },
  metaLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: '#9CA3AF',
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  metaVal: {
    fontSize: 13,
    fontWeight: '600',
    color: '#111827',
  },

  /* ACTION BUTTONS */
  actionBtnRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
  },

  disconnectBtn: {
    backgroundColor: '#FEE2E2',
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 14,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
  },
  disconnectBtnText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#DC2626',
  },

  updateBtn: {
    backgroundColor: '#F3F4F6',
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 14,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
  },
  updateBtnText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
  },
});
